import React, { useEffect, useState } from "react";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Button from "react-bootstrap/Button";
import NavBar from "../../../../components/navbar/NavBar";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Switch from "react-bootstrap/Switch";
import Swal from "sweetalert2";
import messages from "../../../../constants/messages";
import IDL from "../../../../constants/token_idl.json";
import { Program, BN, AnchorProvider } from "@coral-xyz/anchor";
import { connection } from "../../../../constants/constants";
import { Transaction, PublicKey } from "@solana/web3.js";
import useLocalWallet from "../../../../utils/hooks";
import { useWallet } from "@solana/wallet-adapter-react";
import useContractInit from "../../../../utils/hooks/contractInit";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-regular-svg-icons";
import Tooltip from "@mui/material/Tooltip";

const AdminSettings = () => {
  const {
    connected,
    walletAddress,
    balance,
    isOpen,
    setIsOpen,
    handleRightToggle,
    copyAccountToClipboard,
  } = useLocalWallet();
  const wallet = useWallet();

  const {
    deploymentWalletAddress,
    tokenSaleConfigAddress,
    getTokenInitializationDetails,
  } = useContractInit();

  const navigate = useNavigate();
  const [inputToken, setInputToken] = useState("");
  const [savedToken, setSavedToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [isLimit, setIsLimit] = useState(false);
  const [error, setError] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const user = useSelector((state) => state.userStore.user);
  const token = user.auth;

  const handleToggle = () => {
    setIsLimit(!isLimit);
    setShowMessage(true);
  };

  const [program, setProgram] = useState(null);

  const TOKEN_SALE_PROGRAM_ID = new PublicKey(
    process.env.REACT_APP_TOKEN_SALE_PROGRAM_ID
  );

  const TokenSaleAuthority = process.env.REACT_APP_TOKEN_SALE_AUTHORITY;

  const tokenPrice = process.env.REACT_APP_TOKEN_PRICE;

  const transaction = new Transaction();

  // Initialize Anchor program
  useEffect(() => {
    const initializeProgram = async () => {
      if (wallet) {
        try {
          const provider = new AnchorProvider(connection, wallet, {
            commitment: "confirmed",
          });

          const programInstance = new Program(IDL, provider);
          setProgram(programInstance);
        } catch (error) {
          console.error("Error creating Program: ", error);
        }
      } else {
        return;
      }
    };

    initializeProgram();
  }, []);

  const handleSetLimit = async () => {
    let tokenInput = inputToken;
    if (isLimit === false) {
      tokenInput = 700000000;
      setInputToken(tokenInput);
    }
    if (tokenInput === "" || tokenInput === null || tokenInput === undefined) {
      setError(messages.errors.tokenLimitRequired);
      return;
    }
    if (tokenInput < 0) {
      setError(messages.errors.errorTokenLimit);
      return;
    }
    const connectedWallet = wallet?.publicKey?.toBase58();
    if (deploymentWalletAddress !== connectedWallet) {
      Swal.fire({
        title: "Wallet Mismatch",
        text: "The connected wallet doesn't match the wallet used during token creation. please connect the same wallet to proceed with token limit setting.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }
    setLoading(true);
    setError("");
    setShowMessage(false);

    const details = {
      tokenQuantity: tokenInput,
      status: isLimit,
    };
    try {
      const newLimit = tokenInput * 1000000;
      const _newLimit = new BN(newLimit);

      const ix = await program.methods
        .setPurchaseLimit(_newLimit)
        .accounts({
          authority: wallet.publicKey,
          saleConfig: tokenSaleConfigAddress,
        })
        .instruction();
      transaction.add(ix);

      let recentBlockhash = (await connection.getLatestBlockhash()).blockhash;
      transaction.recentBlockhash = recentBlockhash;

      transaction.feePayer = wallet.publicKey;

      const signature = await window.solana.signAndSendTransaction(transaction);

      await connection.confirmTransaction(signature.signature, "confirmed");

      const _sign = signature?.signature;
      details.transactionId = _sign;

      await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT_URL}/token/setTokenBuyLimit`,
        details,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSavedToken(tokenInput);
      const message = isLimit
        ? `You have successfully set the daily token purchase limit to ${tokenInput} $LAL per user.`
        : messages.success.successUnsetLimit;

      Swal.fire({
        title: messages.titles.success,
        text: `${message}`,
        icon: "success",
        confirmButtonText: messages.buttons.ok,
      });
    } catch (error) {
      console.error("error", error);

      Swal.fire({
        title: messages.titles.error,
        text: messages.errors.limitSetFailed,
        icon: "error",
        confirmButtonText: messages.buttons.ok,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchLimit = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_ENDPOINT_URL}/token/getAdminTokenBuyLimit`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setIsLimit(response.data?.data?.tokenBuyLimitStatus || false);
        setInputToken(
          response.data?.data?.dailyTokenBuyLimit !== undefined
            ? response.data.data.dailyTokenBuyLimit
            : ""
        );
        setSavedToken(
          response.data?.data?.dailyTokenBuyLimit !== undefined
            ? response.data.data.dailyTokenBuyLimit
            : ""
        );
      } catch (error) {
        Swal.fire({
          title: messages.titles.error,
          text: messages.errors.fetchLimitFailed,
          icon: "error",
          confirmButtonText: messages.buttons.ok,
        });
      }
    };

    fetchLimit();
  }, [navigate]);

  useEffect(() => {
    getTokenInitializationDetails();
  }, []);

  const handleConnectWallet = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <NavBar />
      <div className={`sidebar-sec ${isOpen === true ? "active" : ""}`}>
        <section id="content-wrapper">
          <h4 className="my-dash main-head">Configure Purchase Limits</h4>
          <div className="right-sidebar">
            <div className="right-sidebar-inner">
              <nav className="navbar navbar-expand-lg navbar-light">
                <div className="right-toggle-menu-outer">
                  <div className="right-toggle-menu">
                    <div
                      className="right-toggle-menu-inner"
                      onClick={handleRightToggle}
                    >
                      <div className="wallent">
                        {connected && walletAddress ? (
                          <button className="btn btn-one">
                            {walletAddress.substring(0, 4)}...
                            {walletAddress.slice(-4)}
                          </button>
                        ) : (
                          <button className="btn btn-two">Connect</button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </nav>
              <div
                className={`right-sidebar ${isOpen === true ? "active" : ""}`}
              >
                <div className="sd-header">
                  <div className="sd-header-btn" onClick={handleRightToggle}>
                    <i className="fa fa-times"></i>
                  </div>
                </div>
                <div className="sd-body">
                  <div className="row">
                    <div className="col-lg-12 col-sm-12">
                      <div className="connect-card">
                        <div className="card-body">
                          <div className="card-inner">
                            <div className="wallet-ui">
                              <div className="wallet-add">
                                <h6
                                  className={`wallet-address ${
                                    connected && walletAddress
                                      ? "wallet-connected"
                                      : "wallet-disconnected"
                                  }`}
                                >
                                  {connected && walletAddress
                                    ? "Wallet Address "
                                    : ""}
                                  {connected && walletAddress
                                    ? `${walletAddress.substring(
                                        0,
                                        4
                                      )}...${walletAddress.slice(-4)}`
                                    : "Wallet not connected"}
                                </h6>
                                {connected && walletAddress ? (
                                  <Tooltip title="Copy Wallet Address" arrow>
                                    <FontAwesomeIcon
                                      icon={faCopy}
                                      className="admin-lfc-deposit-copy"
                                      onClick={copyAccountToClipboard}
                                    />
                                  </Tooltip>
                                ) : (
                                  ""
                                )}
                              </div>
                              <div className="admin-lfc-deposit-balance">
                                {connected ? (
                                  <p>Balance: {balance} SOL</p>
                                ) : (
                                  <p>Balance: 0 SOL</p>
                                )}
                              </div>
                              <WalletMultiButton />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={`sidebar-overlay ${isOpen === true ? "active" : ""}`}
                onClick={handleRightToggle}
              ></div>
            </div>
          </div>
          <div className="user-dashboard-inner">
            <div className="user-dashboard-tab">
              <div className="container">
                <div className="user-dashboard-tab-inner">
                  <Tabs
                    defaultActiveKey="limit"
                    id="uncontrolled-tab-example"
                    className=""
                  >
                    <Tab eventKey="limit" title="Purchase Limit">
                      <div className="user-dash-tab-form">
                        <div className="token-limit-display mb-2">
                          {savedToken !== "" && savedToken !== undefined ? (
                            <div className="mb-3 solana-display">
                              <i className="fa fa-arrow-right arrow-price"></i>
                              <h6>
                                Daily Token Purchase limit per user:
                                {savedToken} $LAL
                              </h6>
                            </div>
                          ) : (
                            <div className="mb-3 solana-display">
                              <i className="fa fa-arrow-right arrow-price"></i>
                              <h6>
                                Buy Token limit is not set currently. You can
                                set it below
                              </h6>
                            </div>
                          )}
                        </div>
                        <Form.Label>
                          Set Token Purchase Limit<span>*</span>
                        </Form.Label>
                        <InputGroup className="">
                          <Form.Control
                            type="number"
                            name="token"
                            aria-label="Number input with dropdown button"
                            value={inputToken}
                            onChange={(e) => {
                              setInputToken(e.target.value);
                              setError("");
                            }}
                            placeholder="Enter Token Amount"
                            required
                          />
                        </InputGroup>
                      </div>
                      <div className="mb-2">
                        {error && <div className="error-limit">{error}</div>}
                      </div>
                      <div className="connect-wallet-one">
                        <Switch
                          checked={isLimit}
                          onChange={handleToggle}
                          label="Enable/Disable Token Purchase Limit"
                        />
                      </div>
                      {showMessage && (
                        <span className="admin-setting-text">
                          You are about to
                          {isLimit
                            ? " enable token limit. "
                            : " disable token limit. "}
                          To apply this change, please click on Submit
                        </span>
                      )}
                      {connected ? (
                        <div className="connect-wallet-one">
                          <Button onClick={handleSetLimit} disabled={loading}>
                            {loading ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            ) : (
                              "Submit"
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="connect-wallet-one">
                          <button onClick={handleConnectWallet}>
                            Connect Wallet
                          </button>
                        </div>
                      )}
                    </Tab>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AdminSettings;
